// ** React Imports
import { useState, useEffect, ChangeEvent } from 'react'

// ** Type Imports
import { DatacenterOption, MoengageAnalyticsType, IdentityOptionType } from 'src/lib/types/integrations'

// ** Child Imports
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { Switch, Grid, Typography, TextField } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig, verifyAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialMoengageAnalyticsState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import { Identity, clevertapRegionOption, moengageDataCenterOptions } from 'src/assets/SettingsConfig'
import { useLocale } from 'src/lib/hooks/useLocal'
import { getEnabledChannelsArray } from 'src/lib/utils/parsers'

interface MoengageProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const Moengage: React.FC<MoengageProps> = ({ isOnboarding = false, handleOnboardingSave }) => {
  // Context hook
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { t } = useLocale()
  const { moengage } = integrations || {}

  //Local State
  const [moengageAnalytics, setMoengageAnalytics] = useState<MoengageAnalyticsType>(initialMoengageAnalyticsState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary) return
    setMoengageAnalytics(temporary.integrations?.moengage)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(moengage) !== JSON.stringify(moengageAnalytics)))
  }, [moengage, moengageAnalytics])

  useEffect(() => {
    if (!moengage) return
    setMoengageAnalytics(moengage)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [moengage, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            moengage: moengageAnalytics
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const isValidForm = () => {
    if (
      moengageAnalytics?.enabled &&
      !Boolean(
        moengageAnalytics?.appId?.length ||
          !Boolean(moengageAnalytics?.dataAPIId?.length || !Boolean(moengageAnalytics?.dataAPIKey?.length))
      )
    ) {
      setOpenAlert('error')
      setAlertMessage('Please fill required moengage analytics fields!')
      return false
    }
    return true
  }

  const callSubmit = async () => {
    if (!isValidForm()) return
    const payload = {
      enabled: moengageAnalytics?.enabled,
      app_id: moengageAnalytics?.appId?.trim(),
      data_center: moengageAnalytics?.dataCenter,
      data_api_id: moengageAnalytics?.dataAPIId?.trim(),
      data_api_key: moengageAnalytics?.dataAPIKey?.trim(),
      customer_id_type: moengageAnalytics?.customerIdType,
      channels: getEnabledChannelsArray(moengageAnalytics?.channels)
    }
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const verifyResponse: any = await verifyAnalyticsConfig('moengage', payload)
      if (verifyResponse?.error) {
        setOpenAlert('error')
        setAlertMessage(verifyResponse?.error)
        console.error('x-trace-id:', verifyResponse.headers?.['x-trace-id'])
        setLoaderProperties({ isLoaderVisible: false })
        return
      }

      const response: any = await setAnalyticsConfig('moengage', payload)
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations({ ...integrations, moengage: moengageAnalytics }, false)
        setOpenAlert('success')
        setAlertMessage('Successfully saved analytics configuration!')
        handleOnboardingSave && handleOnboardingSave()
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errorMessage
        ? error?.response?.data?.errorMessage
        : `Could not save configuration!`
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      console.error(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleDiscardSaving = () => {
    if (!moengage) return
    setMoengageAnalytics(moengage)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <IntegrationChannels
        channels={moengageAnalytics.channels ?? []}
        onChange={(channels: any) => {
          setMoengageAnalytics({ ...moengageAnalytics, channels: channels })
        }}
        hideChannels={!moengageAnalytics?.enabled}
      >
        <Grid display={'flex'} flexDirection={'column'} width={'100%'}>
          {/* <Typography variant='body1' sx={{ fontWeight: 600 }}>
                    Webengage analytics
                  </Typography> */}

          <Grid item xs={12} md={12} margin={'1rem 0rem'} display={'flex'} flexDirection={'column'} gap={2}>
            <Grid display={'flex'} alignItems={'center'} gap={36}>
              <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                App ID:
              </Typography>
              <TextField
                fullWidth
                type='text'
                size='small'
                variant='outlined'
                sx={{ maxWidth: '400px' }}
                value={moengageAnalytics?.appId}
                placeholder='App ID'
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setMoengageAnalytics({ ...moengageAnalytics, appId: event?.target?.value })
                }
              />
            </Grid>
            <Grid display={'flex'} alignItems={'center'} gap={28}>
              <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                Data API ID:
              </Typography>
              <TextField
                fullWidth
                type='text'
                size='small'
                variant='outlined'
                sx={{ maxWidth: '400px' }}
                value={moengageAnalytics?.dataAPIId}
                inputProps={{ 'aria-label': 'controlled' }}
                placeholder='Data API ID'
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setMoengageAnalytics({ ...moengageAnalytics, dataAPIId: event?.target?.value })
                }
              />
            </Grid>
            <Grid display={'flex'} alignItems={'center'} gap={25}>
              <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                Data API Key:
              </Typography>
              <TextField
                fullWidth
                type='text'
                size='small'
                variant='outlined'
                sx={{ maxWidth: '400px' }}
                value={moengageAnalytics?.dataAPIKey}
                inputProps={{ 'aria-label': 'controlled' }}
                placeholder='Data API Key'
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setMoengageAnalytics({ ...moengageAnalytics, dataAPIKey: event?.target?.value })
                }
              />
            </Grid>
            <Grid display={'flex'} alignItems={'center'} gap={27}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography variant='body2'>Data Center</Typography>
              </div>
              <DropdownButton
                options={moengageDataCenterOptions}
                handleClick={(index: number) =>
                  setMoengageAnalytics({
                    ...moengageAnalytics,
                    dataCenter: moengageDataCenterOptions?.[index]?.value
                  })
                }
                variant='outlined'
                title={'Data center'}
                selectedOption={moengageDataCenterOptions?.findIndex(
                  (option: DatacenterOption, index: number) => option?.value === moengageAnalytics?.dataCenter
                )}
                size='large'
              />
            </Grid>
            <Grid display={'flex'} alignItems={'center'} gap={17}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography variant='body2'>Customer ID type:</Typography>
              </div>
              <div className='w-[360px]'>
                <DropdownButton
                  options={Identity}
                  handleClick={(index: number) =>
                    setMoengageAnalytics({
                      ...moengageAnalytics,
                      customerIdType: Identity?.[index]?.value
                    })
                  }
                  variant='outlined'
                  title={'Type'}
                  selectedOption={Identity?.findIndex(
                    (option: IdentityOptionType, index: number) => option?.value === moengageAnalytics?.customerIdType
                  )}
                  size='large'
                />
              </div>
            </Grid>
          </Grid>

          <Grid item xs={12} display={'flex'} alignItems={'center'}>
            <Grid container>
              <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
                <Typography variant='body2'>Moengage analytics is</Typography>
                <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                  {`${moengageAnalytics?.enabled ? 'enabled' : 'disabled'}`}
                </Typography>
              </Grid>
              <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
                <Switch
                  name='moengageToggle'
                  checked={moengageAnalytics?.enabled}
                  inputProps={{ 'aria-label': 'controlled' }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setMoengageAnalytics({ ...moengageAnalytics, enabled: event.target.checked })
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </IntegrationChannels>

      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={callSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <button
          className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
          onClick={callSubmit}
        >
          {t('save')}
        </button>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}
export default Moengage
